import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  QrCode, Nfc, Keyboard, Camera, Users, Wallet, Search,
  Sparkles, ShieldCheck, Zap, X, CreditCard, ScanLine, Radio,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { fmtUSD } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useEffectiveBrand, type EffectiveBrand } from "@/lib/tenant-branding";

declare global {
  interface Window { NDEFReader?: any }
}

type Mode = "demo" | "qr" | "nfc" | "manual";

/** Accept student card: QR (camera or paste) OR NFC tap OR physical card. Stunning cinematic UX. */
export function CardSwipe({ onResolve, trigger }: { onResolve: (wallet: { id: string; student_id?: string; balance_ugx: number; full_name?: string }) => void; trigger?: React.ReactNode }) {
  const { brand } = useEffectiveBrand();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("demo");
  const [manual, setManual] = useState("");
  const [status, setStatus] = useState<string>("");
  const [resolved, setResolved] = useState<{ id: string; student_id?: string; balance_ugx: number; full_name?: string; class_label?: string; initials?: string; color_hex?: string } | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const detectorRef = useRef<any>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scanRafRef = useRef<number | null>(null);

  const resolve = async (token: string) => {
    setStatus("Resolving card…");
    let walletId: string | null = null;
    let studentId: string | undefined;
    const { data: card } = await supabase
      .from("student_cards")
      .select("wallet_id, student_id")
      .or(`qr_token.eq.${token},nfc_uid.eq.${token}`)
      .maybeSingle();
    if (card) { walletId = card.wallet_id; studentId = card.student_id; }
    else {
      // Try: token is a student id → find their wallet
      const { data: st } = await supabase.from("students").select("id").eq("id", token.trim()).maybeSingle();
      if (st) {
        studentId = st.id;
        const { data: sw } = await supabase.from("wallets").select("id").eq("owner_type", "student").eq("owner_id", st.id).maybeSingle();
        if (sw) walletId = sw.id;
      }
      if (!walletId) {
        // Fall back: token is a raw wallet UUID
        const { data: w } = await supabase.from("wallets").select("id").eq("id", token.trim()).maybeSingle();
        if (w) walletId = w.id;
      }
    }
    if (!walletId) { toast.error("Card not recognised"); setStatus(""); return; }
    const { data: wallet } = await supabase.from("wallets").select("id, balance_ugx").eq("id", walletId).single();
    let student: any = null;
    if (studentId) {
      const { data: s } = await supabase
        .from("students")
        .select("full_name, class_label, initials, color_hex")
        .eq("id", studentId)
        .maybeSingle();
      student = s;
    }
    if (wallet) {
      const payload = {
        id: wallet.id,
        student_id: studentId,
        balance_ugx: wallet.balance_ugx,
        full_name: student?.full_name,
        class_label: student?.class_label,
        initials: student?.initials,
        color_hex: student?.color_hex,
      };
      setResolved(payload);
      stopScans();
      // Celebrate for a moment, then hand back to the POS
      setTimeout(() => {
        onResolve(payload);
        setOpen(false);
        toast.success(`Card accepted${student?.full_name ? ` · ${student.full_name}` : ""}`);
      }, 900);
    }
  };

  const startQr = async () => {
    setStatus("Starting camera…");
    try {
      const Detector = (window as any).BarcodeDetector;
      if (!Detector) { setStatus("Your browser can't scan. Paste the token in Manual."); return; }
      detectorRef.current = new Detector({ formats: ["qr_code"] });
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
      setStatus("Line up the QR inside the frame.");
      const tick = async () => {
        if (!videoRef.current) return;
        try {
          const codes = await detectorRef.current.detect(videoRef.current);
          if (codes[0]?.rawValue) { await resolve(codes[0].rawValue); return; }
        } catch {}
        scanRafRef.current = requestAnimationFrame(tick);
      };
      scanRafRef.current = requestAnimationFrame(tick);
    } catch (e: any) { setStatus("Camera blocked: " + e.message); }
  };

  const startNfc = async () => {
    setStatus("Hold the card flat against the back of your phone…");
    if (!window.NDEFReader) { setStatus("Web NFC is Chrome/Android only. Use QR instead."); return; }
    try {
      const reader = new window.NDEFReader();
      await reader.scan();
      reader.onreading = async (ev: any) => {
        const uid = ev.serialNumber || ev.message?.records?.[0]?.data;
        if (uid) await resolve(String(uid));
      };
    } catch (e: any) { setStatus("NFC error: " + e.message); }
  };

  const stopScans = () => {
    if (scanRafRef.current) cancelAnimationFrame(scanRafRef.current);
    scanRafRef.current = null;
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  };

  const switchMode = (m: Mode) => {
    stopScans();
    setStatus("");
    setMode(m);
    if (m === "qr") setTimeout(startQr, 50);
    if (m === "nfc") setTimeout(startNfc, 50);
  };

  useEffect(() => {
    if (!open) { stopScans(); setResolved(null); setMode("demo"); setManual(""); }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <div onClick={() => setOpen(true)}>{trigger ?? <Button variant="outline"><QrCode className="mr-2 h-4 w-4" />Accept card</Button>}</div>
      <DialogContent className="max-w-4xl overflow-hidden border-0 p-0 sm:rounded-2xl">
        <div className="grid grid-cols-1 md:grid-cols-[1.05fr_1fr]">
          {/* ─────────── LEFT: cinematic reader ─────────── */}
          <div
            className="relative overflow-hidden p-6 text-white"
            style={{
              background: `radial-gradient(120% 120% at 0% 0%, color-mix(in oklab, ${brand.primaryColor} 65%, #05070f) 0%, color-mix(in oklab, ${brand.primaryColor} 30%, #05070f) 55%, #05070f 100%)`,
            }}
          >
            {/* aurora blobs — tinted with tenant brand */}
            <div
              className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full blur-3xl"
              style={{ background: `${brand.accentColor}33` }}
            />
            <div
              className="pointer-events-none absolute -bottom-24 -right-16 h-72 w-72 rounded-full blur-3xl"
              style={{ background: `${brand.primaryColor}44` }}
            />
            <div className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.6) 1px,transparent 1px)", backgroundSize: "22px 22px" }} />

            <div className="relative flex items-center gap-2">
              <div className="grid h-8 w-8 place-items-center rounded-lg bg-white/10 backdrop-blur">
                {brand.logoUrl ? (
                  <img src={brand.logoUrl} alt={brand.name} className="h-5 w-5 rounded object-cover" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/60">{brand.name} Pay · Reader</div>
                <div className="text-sm font-semibold">Accept student card</div>
              </div>
              <Badge className="ml-auto border-emerald-400/30 bg-emerald-400/10 text-[10px] text-emerald-300">
                <span className="mr-1 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />LIVE
              </Badge>
            </div>

            {/* Stage */}
            <div className="relative mt-6 aspect-[4/5] w-full overflow-hidden rounded-2xl border border-white/10 bg-black/40">
              {resolved ? (
                <ResolvedCard resolved={resolved} brand={brand} />
              ) : mode === "qr" ? (
                <QrStage videoRef={videoRef} brand={brand} />
              ) : mode === "nfc" ? (
                <NfcStage brand={brand} />
              ) : mode === "manual" ? (
                <ManualStage />
              ) : (
                <PhysicalCardStage brand={brand} />
              )}
            </div>

            <div className="relative mt-4 flex items-center gap-2 text-[11px] text-white/60">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-300" />
              <span>Tokenised · PCI-safe · No PAN stored</span>
              <Zap className="ml-auto h-3.5 w-3.5 text-amber-300" />
              <span>~120ms average tap</span>
            </div>

            {status && !resolved && (
              <div className="relative mt-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-[11px] text-white/80 backdrop-blur">
                {status}
              </div>
            )}
          </div>

          {/* ─────────── RIGHT: mode picker + content ─────────── */}
          <div className="relative flex flex-col bg-background p-6">
            <button onClick={() => setOpen(false)} className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full text-muted-foreground transition hover:bg-muted"><X className="h-4 w-4" /></button>

            <div className="text-xs uppercase tracking-widest text-muted-foreground">Choose method</div>
            <h2 className="mt-1 text-2xl font-bold tracking-tight">How is the card presented?</h2>
            <p className="mt-1 text-sm text-muted-foreground">Physical NFC cards, QR-only cards, or search the demo class list.</p>

            <div className="mt-5 grid grid-cols-2 gap-2">
              <ModeTile active={mode === "demo"}   icon={<Users className="h-4 w-4" />}    label="Demo class"     hint="Pick a seeded student"          onClick={() => switchMode("demo")} />
              <ModeTile active={mode === "qr"}     icon={<Camera className="h-4 w-4" />}   label="QR card"        hint="Scan with camera"              onClick={() => switchMode("qr")} />
              <ModeTile active={mode === "nfc"}    icon={<Nfc className="h-4 w-4" />}      label="NFC / tap"      hint="Physical contactless card"     onClick={() => switchMode("nfc")} />
              <ModeTile active={mode === "manual"} icon={<Keyboard className="h-4 w-4" />} label="Manual token"   hint="Type or paste ID"              onClick={() => switchMode("manual")} />
            </div>

            <div className="mt-5 flex-1 overflow-auto">
              {mode === "demo" && <DemoCards onPick={(t) => resolve(t)} />}
              {mode === "manual" && (
                <div className="space-y-3 rounded-xl border bg-muted/30 p-4">
                  <Label className="text-xs">Wallet ID or card token</Label>
                  <Input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="paste UUID or scanned token" />
                  <Button className="w-full" disabled={!manual} onClick={() => resolve(manual.trim())}>Look up card</Button>
                  <div className="text-[10px] text-muted-foreground">Use this when the reader is offline or the card is damaged.</div>
                </div>
              )}
              {mode === "qr" && (
                <div className="rounded-xl border bg-muted/20 p-4 text-xs text-muted-foreground">
                  Hold the student's card so the QR fills the framing box. Auto-captures once decoded.
                </div>
              )}
              {mode === "nfc" && (
                <div className="rounded-xl border bg-muted/20 p-4 text-xs text-muted-foreground">
                  Works on Android Chrome. Tap the card to the back of the phone/reader — it reads in under 200ms.
                </div>
              )}
            </div>

            <div className="mt-4 flex items-center justify-between border-t pt-3 text-[10px] text-muted-foreground">
              <span>Reader v3.2 · {brand.name}</span>
              <span className="flex items-center gap-1"><Wallet className="h-3 w-3" /> settles to wallet_ledger_v2</span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────── stages ─────────────── */

function PhysicalCardStage({ brand }: { brand: EffectiveBrand }) {
  return (
    <div className="grid h-full place-items-center p-4">
      <div className="relative h-full w-full">
        {/* Tenant-branded card mock */}
        <div
          className="absolute left-1/2 top-1/2 h-52 w-80 -translate-x-1/2 -translate-y-1/2 rotate-[-14deg] overflow-hidden rounded-2xl p-5 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.85),inset_0_1px_0_rgba(255,255,255,0.08)]"
          style={{
            background: `radial-gradient(120% 120% at 20% 0%, color-mix(in oklab, ${brand.primaryColor} 80%, #ffffff) 0%, ${brand.primaryColor} 55%, color-mix(in oklab, ${brand.primaryColor} 60%, #000000) 100%)`,
          }}
        >
          {/* embossed micro-texture */}
          <div
            className="pointer-events-none absolute inset-0 opacity-40 mix-blend-overlay"
            style={{
              backgroundImage:
                "radial-gradient(circle at 25% 30%, rgba(255,255,255,0.14) 0.5px, transparent 1.5px), radial-gradient(circle at 75% 70%, rgba(0,0,0,0.3) 0.5px, transparent 1.5px)",
              backgroundSize: "6px 6px, 8px 8px",
            }}
          />
          {/* soft top-left highlight */}
          <div className="pointer-events-none absolute -left-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" />

          {/* Logo mark */}
          <div className="relative mx-auto mt-3 grid place-items-center">
            {brand.logoUrl ? (
              <img
                src={brand.logoUrl}
                alt={brand.name}
                className="h-14 w-14 rounded-xl object-cover shadow-md ring-1 ring-white/20"
              />
            ) : (
              <div
                className="grid h-14 w-14 place-items-center rounded-xl text-xl font-black text-white shadow-md ring-1 ring-white/20"
                style={{ background: `linear-gradient(135deg, ${brand.accentColor}, ${brand.primaryColor})` }}
              >
                {brand.name.slice(0, 2).toUpperCase()}
              </div>
            )}
            <div
              className="mt-2 text-lg font-black tracking-tight"
              style={{
                background: "linear-gradient(180deg,#f5f5f5,#8f939a)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
                textShadow: "0 1px 0 rgba(0,0,0,0.6)",
              }}
            >
              {brand.name}
            </div>
            <div className="-mt-0.5 text-[11px] font-bold tracking-wide text-slate-300/90">Pay</div>
          </div>

          {/* Serial + NFC */}
          <div className="absolute bottom-3 right-4 text-[11px] font-bold tracking-wider text-slate-200/90" style={{ textShadow: "0 1px 0 rgba(0,0,0,0.7)" }}>
            {(brand.name.match(/[A-Z]/g)?.join("").slice(0, 2) || brand.name.slice(0, 2).toUpperCase())}-{Math.floor(1000 + Math.random() * 8999)}
          </div>
          <Nfc className="absolute bottom-3 left-4 h-4 w-4 text-slate-300/80" />
        </div>

        {/* tap ripples */}
        <div className="absolute right-6 top-6 grid place-items-center">
          <div className="absolute h-16 w-16 animate-ping rounded-full border-2 border-emerald-300/40" />
          <div className="relative grid h-10 w-10 place-items-center rounded-full bg-emerald-400/20 text-emerald-200">
            <Radio className="h-4 w-4" />
          </div>
        </div>
        <div className="absolute bottom-4 left-4 right-4 rounded-lg bg-black/50 px-3 py-2 text-[11px] text-white/80 backdrop-blur">
          Waiting for a card — choose <span className="text-white">QR</span>, <span className="text-white">NFC</span>, or the <span className="text-white">demo class</span> on the right.
        </div>
      </div>
    </div>
  );
}

function QrStage({ videoRef, brand }: { videoRef: React.RefObject<HTMLVideoElement>; brand: EffectiveBrand }) {
  return (
    <div className="relative h-full w-full">
      <video ref={videoRef as React.RefObject<HTMLVideoElement>} className="h-full w-full object-cover" muted playsInline />
      {/* framing corners */}
      <div className="pointer-events-none absolute inset-0 grid place-items-center">
        <div className="relative h-56 w-56">
          {["top-0 left-0 border-t-2 border-l-2","top-0 right-0 border-t-2 border-r-2","bottom-0 left-0 border-b-2 border-l-2","bottom-0 right-0 border-b-2 border-r-2"].map((c,i)=>(
            <div key={i} className={cn("absolute h-8 w-8 rounded-[6px]", c)} style={{ borderColor: brand.accentColor }} />
          ))}
          <div className="absolute inset-x-0 top-0 h-full overflow-hidden">
            <div
              className="absolute inset-x-0 h-0.5 animate-[scan_2.4s_ease-in-out_infinite]"
              style={{ background: brand.accentColor, boxShadow: `0 0 18px 2px ${brand.accentColor}b3` }}
            />
          </div>
        </div>
      </div>
      <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-[10px] text-white/80 backdrop-blur">
        <ScanLine className="mr-1 inline h-3 w-3" />auto-capture
      </div>
      <style>{`@keyframes scan{0%{transform:translateY(0)}50%{transform:translateY(220px)}100%{transform:translateY(0)}}`}</style>
    </div>
  );
}

function NfcStage({ brand }: { brand: EffectiveBrand }) {
  return (
    <div className="grid h-full place-items-center">
      <div className="relative grid place-items-center">
        {[0,1,2].map(i => (
          <div key={i} className="absolute rounded-full border-2"
               style={{ width: 80 + i*60, height: 80 + i*60, borderColor: `${brand.accentColor}66`, animation: `pulseRing 2.4s ${i*0.5}s ease-out infinite` }} />
        ))}
        <div
          className="relative grid h-24 w-24 place-items-center rounded-full text-white shadow-[0_0_60px_-5px_rgba(0,0,0,0.5)]"
          style={{ background: `linear-gradient(135deg, ${brand.accentColor}, ${brand.primaryColor})` }}
        >
          <Nfc className="h-10 w-10" />
        </div>
      </div>
      <style>{`@keyframes pulseRing{0%{opacity:0.9;transform:scale(0.6)}100%{opacity:0;transform:scale(1.2)}}`}</style>
    </div>
  );
}

function ManualStage() {
  return (
    <div className="grid h-full place-items-center p-6 text-center">
      <div>
        <CreditCard className="mx-auto h-14 w-14 text-white/60" />
        <div className="mt-3 text-sm text-white/70">Offline / fallback lookup</div>
        <div className="mt-1 text-[11px] text-white/50">Enter the token printed on the card or the wallet UUID.</div>
      </div>
    </div>
  );
}

function ResolvedCard({ resolved, brand }: { resolved: any; brand: EffectiveBrand }) {
  const initials = resolved.initials || resolved.full_name?.slice(0, 2).toUpperCase() || "ST";
  const color = resolved.color_hex || brand.accentColor;
  return (
    <div className="relative grid h-full place-items-center p-6">
      <div className="absolute inset-0" style={{ background: `radial-gradient(60% 60% at 50% 40%, ${color}55, transparent 70%)` }} />
      <div className="relative w-full max-w-xs rounded-2xl border border-white/10 bg-gradient-to-br from-white/10 to-white/5 p-5 text-center backdrop-blur">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-full text-lg font-bold text-white shadow-lg" style={{ background: color }}>
          {initials}
        </div>
        <div className="mt-3 text-lg font-semibold">{resolved.full_name || "Student"}</div>
        <div className="text-[11px] text-white/60">{resolved.class_label || "Class"}</div>
        <div className="mt-4 rounded-lg border border-emerald-400/30 bg-emerald-400/10 py-2 text-emerald-300">
          <div className="text-[10px] uppercase tracking-widest">Wallet balance</div>
          <div className="font-mono text-xl">{fmtUSD(resolved.balance_ugx)}</div>
        </div>
        <div className="mt-3 flex items-center justify-center gap-1 text-[11px] text-emerald-300">
          <ShieldCheck className="h-3.5 w-3.5" /> Card accepted
        </div>
      </div>
    </div>
  );
}

function ModeTile({ active, icon, label, hint, onClick }: { active: boolean; icon: React.ReactNode; label: string; hint: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className={cn(
      "group relative overflow-hidden rounded-xl border p-3 text-left transition",
      active ? "border-primary bg-primary/5 shadow-sm" : "border-border bg-card hover:border-primary/50"
    )}>
      <div className="flex items-center gap-2">
        <div className={cn("grid h-8 w-8 place-items-center rounded-lg", active ? "bg-primary text-primary-foreground" : "bg-muted text-foreground")}>{icon}</div>
        <div>
          <div className="text-sm font-semibold">{label}</div>
          <div className="text-[10px] text-muted-foreground">{hint}</div>
        </div>
      </div>
      {active && <div className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-primary via-primary to-accent" />}
    </button>
  );
}

/* ─────────────── demo picker ─────────────── */

function DemoCards({ onPick }: { onPick: (token: string) => void }) {
  const [cards, setCards] = useState<Array<{ token: string; name: string; balance: number; class_label?: string; daily_cap?: number; initials?: string; color_hex?: string }>>([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      // Search live students directly — works even if no cards are seeded.
      const { data: students } = await supabase
        .from("students")
        .select("id, full_name, class_label, initials, color_hex, balance_ugx, daily_cap_ugx")
        .order("full_name", { ascending: true })
        .limit(80);
      const list = students ?? [];
      // Pull card tokens for any of them (preferred token when present)
      const ids = list.map((s: any) => s.id);
      const { data: cardsData } = ids.length
        ? await supabase.from("student_cards").select("student_id, qr_token, status").in("student_id", ids).eq("status", "active")
        : { data: [] as any[] };
      const tokenByStudent = new Map<string, string>();
      (cardsData ?? []).forEach((c: any) => tokenByStudent.set(c.student_id, c.qr_token));
      const rows = list.map((s: any) => ({
        token: tokenByStudent.get(s.id) || s.id, // fall back to student.id, resolve() handles it
        name: s.full_name ?? "Student",
        class_label: s.class_label,
        daily_cap: s.daily_cap_ugx,
        balance: s.balance_ugx ?? 0,
        initials: s.initials,
        color_hex: s.color_hex,
      }));
      setCards(rows);
      setLoading(false);
    })();
  }, []);

  const filtered = q.trim() ? cards.filter((c) => c.name.toLowerCase().includes(q.toLowerCase())) : cards;

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search demo students…" className="h-9 pl-7 text-xs" />
      </div>
      <div className="max-h-[320px] space-y-1.5 overflow-auto pr-1">
        {loading && <div className="p-4 text-center text-xs text-muted-foreground">Loading demo cards…</div>}
        {!loading && filtered.length === 0 && (
          <div className="rounded-lg border border-dashed p-4 text-center text-xs text-muted-foreground">
            No demo cards yet. Ask super-admin to seed demo data.
          </div>
        )}
        {filtered.map((c) => (
          <button
            key={c.token}
            onClick={() => onPick(c.token)}
            className="group flex w-full items-center gap-3 rounded-xl border bg-card p-2.5 text-left transition hover:-translate-y-[1px] hover:border-primary hover:shadow-md"
          >
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-sm font-semibold text-white shadow" style={{ background: c.color_hex || "hsl(var(--primary))" }}>
              {c.initials || c.name.slice(0, 1)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{c.name}</div>
              <div className="truncate text-[11px] text-muted-foreground">{c.class_label || "Student"}</div>
            </div>
            <div className="text-right">
              <div className="font-mono text-xs font-semibold">{fmtUSD(c.balance)}</div>
              {c.daily_cap && c.daily_cap > 0 && (
                <Badge variant="outline" className="text-[9px]">cap {fmtUSD(c.daily_cap)}</Badge>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
